/*
 * MarketingFooter
 *
 * Shared marketing footer used across public-facing pages — LandingPage,
 * PricingPage, and the legal pages (Privacy, Terms, etc.).
 *
 * Variants:
 *   - 'dark'  (default) — for use on the dark hero theme (LandingPage, Pricing)
 *   - 'light'           — for use on the white legal pages
 */
import { Linkedin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ryzeLogo from '../../assets/logo.svg';
import { isRtl } from '../../i18n';

interface MarketingFooterProps {
  variant?: 'dark' | 'light';
}

type Link = { l: string; h: string };
type Column = { heading: string; links: Link[] };

export default function MarketingFooter({ variant = 'dark' }: MarketingFooterProps) {
  const { t, i18n } = useTranslation();
  const isDark = variant === 'dark';
  const rtl = isRtl(i18n.resolvedLanguage ?? i18n.language);
  const columns: Column[] = [
    {
      heading: t('marketingFooter.product.heading'),
      links: [
        { l: t('marketingFooter.product.pricing'), h: '/pricing' },
        { l: t('marketingFooter.product.features'), h: '/#features' },
        { l: t('marketingFooter.product.integrations'), h: '/#integrations' },
      ],
    },
    {
      heading: t('marketingFooter.resources.heading'),
      links: [
        { l: t('marketingFooter.resources.help'), h: '/app/help' },
        { l: t('marketingFooter.resources.blog'), h: '/blog' },
        { l: t('marketingFooter.resources.changelog'), h: '/#changelog' },
      ],
    },
    {
      heading: t('marketingFooter.company.heading'),
      links: [
        { l: t('marketingFooter.company.about'), h: '/about' },
        { l: t('marketingFooter.company.customers'), h: '/customers' },
        { l: t('marketingFooter.company.contact'), h: '/contact' },
        { l: t('marketingFooter.company.pressKit'), h: '/press' },
      ],
    },
    {
      heading: t('marketingFooter.legal.heading'),
      links: [
        { l: t('marketingFooter.legal.privacy'), h: '/privacy' },
        { l: t('marketingFooter.legal.terms'), h: '/terms' },
        { l: t('marketingFooter.legal.dpa'), h: '/dpa' },
        { l: t('marketingFooter.legal.security'), h: '/security' },
      ],
    },
  ];

  // theme tokens
  const bg          = isDark ? 'transparent' : '#FAF8F2';
  const borderColor = isDark ? 'rgba(240,237,230,0.06)' : 'rgba(13,11,7,0.08)';
  const headingClr  = isDark ? 'rgba(240,237,230,0.4)' : 'rgba(13,11,7,0.45)';
  const linkClr     = isDark ? 'rgba(240,237,230,0.55)' : 'rgba(13,11,7,0.6)';
  const linkHover   = isDark ? '#F0EDE6' : '#0D0B07';
  const taglineClr  = isDark ? 'rgba(240,237,230,0.5)' : 'rgba(13,11,7,0.6)';
  const muted       = isDark ? 'rgba(240,237,230,0.3)' : 'rgba(13,11,7,0.4)';
  const subBorder   = isDark ? 'rgba(240,237,230,0.06)' : 'rgba(13,11,7,0.06)';
  const statusGreen = '#6EBF8B';

  const socials = [
    { label: t('marketingFooter.linkedInAriaLabel'), href: 'https://www.linkedin.com/company/ryze-mena', icon: <Linkedin className="h-4 w-4" /> },
  ];

  const renderLink = (lnk: Link, key: string) => {
    const isExternal = lnk.h.startsWith('http') || lnk.h.startsWith('mailto');
    const isHash     = lnk.h.startsWith('#');
    const baseStyle = {
      color: linkClr,
    } as const;
    const baseClass = 'block text-sm mb-3 transition-colors';
    const handlers = {
      onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) =>
        ((e.currentTarget as HTMLElement).style.color = linkHover),
      onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) =>
        ((e.currentTarget as HTMLElement).style.color = linkClr),
    };

    if (isExternal || isHash) {
      return (
        <a key={key} href={lnk.h} className={baseClass} style={baseStyle} {...handlers}
           target={isExternal && lnk.h.startsWith('http') ? '_blank' : undefined}
           rel={isExternal && lnk.h.startsWith('http') ? 'noopener noreferrer' : undefined}>
          {lnk.l}
        </a>
      );
    }
    return (
      <a key={key} href={lnk.h} className={baseClass} style={baseStyle} {...handlers}>
        {lnk.l}
      </a>
    );
  };

  return (
    <footer
      dir={rtl ? 'rtl' : 'ltr'}
      className={`px-4 pb-10 pt-16 ${rtl ? 'text-right' : 'text-left'}`}
      style={{ background: bg, borderTop: `1px solid ${borderColor}` }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Top grid — brand + columns */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2">
            <a href="/" className="flex items-center gap-2.5 mb-4 select-none">
              <img
                src={ryzeLogo}
                alt="Ryze Analytics"
                className="h-8 w-auto"
                style={isDark ? { filter: 'invert(1)' } : undefined}
              />
            </a>
            <p className="text-sm leading-relaxed mb-5 max-w-xs" style={{ color: taglineClr }}>
              {t('marketingFooter.tagline')}
            </p>

            {/* Get in touch */}
            <div className="mb-5">
              <div
                className="text-xs font-semibold uppercase tracking-wider mb-2.5"
                style={{ color: headingClr }}
              >
                {t('marketingFooter.getInTouch')}
              </div>
              <div className="space-y-2 text-sm">
                <a href="/contact" className="block transition-colors"
                   style={{ color: linkClr }}
                   onMouseEnter={e => (e.currentTarget.style.color = linkHover)}
                   onMouseLeave={e => (e.currentTarget.style.color = linkClr)}>
                  sales@ryze-group.com
                </a>
                <p className="pt-1 text-sm" style={{ color: muted }}>
                  {t('marketingFooter.locations')}
                </p>
              </div>
            </div>

            {/* Social */}
            <div className="flex items-center gap-2.5 mt-2">
              {socials.map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  target="_blank" rel="noopener noreferrer"
                  className="h-9 w-9 rounded-xl flex items-center justify-center transition-all duration-200"
                  style={{
                    background: isDark ? 'rgba(240,237,230,0.05)' : 'rgba(13,11,7,0.04)',
                    border: `1px solid ${isDark ? 'rgba(240,237,230,0.08)' : 'rgba(13,11,7,0.08)'}`,
                    color: linkClr,
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.background = 'rgba(162,84,79,0.14)';
                    el.style.borderColor = 'rgba(162,84,79,0.4)';
                    el.style.color = isDark ? '#E8A09B' : '#A2544F';
                    el.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.background = isDark ? 'rgba(240,237,230,0.05)' : 'rgba(13,11,7,0.04)';
                    el.style.borderColor = isDark ? 'rgba(240,237,230,0.08)' : 'rgba(13,11,7,0.08)';
                    el.style.color = linkClr;
                    el.style.transform = 'translateY(0)';
                  }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {columns.map(col => (
            <div key={col.heading}>
              <div
                className="text-xs font-semibold uppercase tracking-wider mb-3.5"
                style={{ color: headingClr }}
              >
                {col.heading}
              </div>
              {col.links.map(lnk => renderLink(lnk, lnk.l))}
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div
          className="pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          style={{ borderTop: `1px solid ${subBorder}` }}
        >
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <p className="text-sm" style={{ color: muted }}>
              {t('marketingFooter.copyright')}
            </p>
            <a
              href="https://status.ryze-group.com"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm transition-colors"
              style={{ color: linkClr }}
              onMouseEnter={e => (e.currentTarget.style.color = linkHover)}
              onMouseLeave={e => (e.currentTarget.style.color = linkClr)}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: statusGreen, boxShadow: `0 0 8px ${statusGreen}` }}
              />
              {t('marketingFooter.systemsOperational')}
            </a>
          </div>
          <p className="text-sm" style={{ color: muted }}>
            {t('marketingFooter.madeIn')} <span aria-label={t('marketingFooter.uae')}>🇦🇪</span> {t('marketingFooter.dubaiUae')}
          </p>
        </div>
      </div>
    </footer>
  );
}
